import React from 'react';
import Icon from '../../../components/AppIcon';

const SummaryStats = ({ analysisData }) => {
  // Mock summary statistics
  const stats = {
    totalFundsTraced: 15.75,
    fundsRecovered: 8.25,
    recoveryPercentage: 52.4,
    totalAddresses: 24,
    uniqueTransactions: 18,
    averageConfidence: 0.78,
    highRiskAddresses: 6,
    exchangeDeposits: 3,
    mixingServices: 2,
    analysisTime: '2.3s',
    lastUpdated: new Date()?.toISOString()
  };

  const formatCurrency = (amount) => {
    return `${amount?.toFixed(8)} BTC`;
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const getRecoveryColor = (percentage) => {
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-emerald-600';
    if (confidence >= 0.6) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-forensic-card">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="BarChart3" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Analysis Summary</h3>
          </div>
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="Clock" size={14} />
            <span>Updated {formatTime(stats?.lastUpdated)}</span>
          </div>
        </div>
      </div>
      {/* Main Stats Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Funds Traced */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Total Traced</p>
                <p className="text-2xl font-bold text-text-primary">
                  {formatCurrency(stats?.totalFundsTraced)}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Search" size={24} color="white" />
              </div>
            </div>
          </div>

          {/* Funds Recovered */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Recovered</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(stats?.fundsRecovered)}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={24} color="white" />
              </div>
            </div>
          </div>

          {/* Recovery Percentage */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Recovery Rate</p>
                <p className={`text-2xl font-bold ${getRecoveryColor(stats?.recoveryPercentage)}`}>
                  {formatPercentage(stats?.recoveryPercentage)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={24} color="white" />
              </div>
            </div>
          </div>

          {/* Average Confidence */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Avg Confidence</p>
                <p className={`text-2xl font-bold ${getConfidenceColor(stats?.averageConfidence)}`}>
                  {formatPercentage(stats?.averageConfidence * 100)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} color="white" />
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Address Analysis */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text-primary">Address Analysis</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-text-secondary" />
                  <span className="text-sm text-text-secondary">Total Addresses</span>
                </div>
                <span className="text-sm font-semibold text-text-primary">
                  {stats?.totalAddresses}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-red-500" />
                  <span className="text-sm text-text-secondary">High Risk</span>
                </div>
                <span className="text-sm font-semibold text-red-600">
                  {stats?.highRiskAddresses}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Building2" size={16} className="text-emerald-500" />
                  <span className="text-sm text-text-secondary">Exchange Deposits</span>
                </div>
                <span className="text-sm font-semibold text-emerald-600">
                  {stats?.exchangeDeposits}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Shuffle" size={16} className="text-purple-500" />
                  <span className="text-sm text-text-secondary">Mixing Services</span>
                </div>
                <span className="text-sm font-semibold text-purple-600">
                  {stats?.mixingServices}
                </span>
              </div>
            </div>
          </div>

          {/* Transaction Analysis */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text-primary">Transaction Analysis</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="ArrowRightLeft" size={16} className="text-text-secondary" />
                  <span className="text-sm text-text-secondary">Unique Transactions</span>
                </div>
                <span className="text-sm font-semibold text-text-primary">
                  {stats?.uniqueTransactions}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={16} className="text-amber-500" />
                  <span className="text-sm text-text-secondary">Analysis Time</span>
                </div>
                <span className="text-sm font-semibold text-amber-600">
                  {stats?.analysisTime}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Activity" size={16} className="text-blue-500" />
                  <span className="text-sm text-text-secondary">Pattern Detection</span>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  Active
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={16} className="text-text-secondary" />
                  <span className="text-sm text-text-secondary">Data Sources</span>
                </div>
                <span className="text-sm font-semibold text-text-primary">
                  5 APIs
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-text-primary mb-4">Recovery Progress</h4>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-text-secondary">Funds Recovered</span>
                <span className="text-sm font-semibold text-text-primary">
                  {formatPercentage(stats?.recoveryPercentage)}
                </span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getRecoveryColor(stats?.recoveryPercentage)?.replace('text-', 'bg-')}`}
                  style={{ width: `${stats?.recoveryPercentage}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-text-secondary">Analysis Confidence</span>
                <span className="text-sm font-semibold text-text-primary">
                  {formatPercentage(stats?.averageConfidence * 100)}
                </span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getConfidenceColor(stats?.averageConfidence)?.replace('text-', 'bg-')}`}
                  style={{ width: `${stats?.averageConfidence * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryStats;